import React from 'react';
import { ResourcePostData } from '../types';

export const professionalPresenterPlaybook: ResourcePostData = {
    id: "professional-presenter-playbook-2026",
    series: "Professional Presenter",
    title: "The Professional Presenter's Playbook: AI Proposals & Board Presentations",
    date: "April 2026",
    readTime: "18 min read",
    tags: ["ai sales proposals", "board presentations", "ai presentations", "cited data", "enterprise sales", "deckwale", "presentation best practices 2026", "sales proposal guide", "board deck best practices"],
    sections: [
        {
            id: "introduction",
            title: "Introduction",
            content: (
                <>
                    <p>
                        The way professionals build, share, and defend presentations has changed completely. Most people are still catching up.
                    </p>
                    <p>
                        Three things determine whether a presentation actually moves the needle in 2026 - whether it closes the deal, gets the budget approved, or convinces the board to say yes.
                    </p>
                    <p>
                        First: whether your proposal is built around your audience's problem, not your solution's features. Second: whether you know what to say when someone challenges your data. Third: whether you know what happened after you hit send - who read it, what they focused on, and where they dropped off.
                    </p>
                    <p>
                        Every section of this guide addresses one of those three things. We'll cover how to use AI to write sales proposals that actually close deals, how to build board presentations that get approved in the first meeting, and why research citations in AI-generated decks have gone from "nice to have" to non-negotiable for anyone selling to enterprise buyers.
                    </p>
                    <p>
                        Read all three. They work together.
                    </p>
                </>
            )
        },
        {
            id: "part-one-sales-proposals",
            title: "Part One: How to Use AI for Sales Proposals That Close Deals",
            content: (
                <>
                    <h4 className="text-xl font-bold text-gray-900 mt-6 mb-4">Why Most AI-Generated Proposals Still Lose</h4>
                    <p>
                        Here's the uncomfortable truth about AI sales proposals in 2026: most of them are failing for the same reason human-written proposals failed before them. They're built around the seller's story, not the buyer's problem.
                    </p>
                    <p>
                        Sales reps finally have AI tools that can write a proposal in minutes. But if the underlying structure is wrong - if the first page leads with your company history instead of the buyer's pain, if you bury the pricing on page seven, if the whole document reads like a product brochure with their logo swapped in - the AI just writes that failure faster.
                    </p>
                    <p>
                        The proposals that close deals are different. They're structured around the buyer's specific situation, the cost of their current problem, and the concrete outcome your solution delivers. They're short enough to be read in full. They're backed by data that stands up to scrutiny. And they're tracked so the rep knows when to follow up and what to say.
                    </p>

                    <h4 className="text-xl font-bold text-gray-900 mt-8 mb-4">Step 1: Feed the AI the Right Raw Material</h4>
                    <p>
                        The biggest mistake when using AI to write a sales proposal is starting with a blank prompt and asking for a generic proposal. You'll get generic output.
                    </p>
                    <p>
                        Before you touch the AI, gather three things from your discovery process:
                    </p>
                    <ul className="list-disc pl-5 space-y-4 mb-6">
                        <li><strong>The buyer's problem in their own words.</strong> Pull exact phrases from call transcripts, email threads, or meeting notes. The AI can't invent the buyer's specific language - you have to give it.</li>
                        <li><strong>The consequence of inaction.</strong> What happens if the buyer does nothing? What does the problem cost them monthly, quarterly, annually?</li>
                        <li><strong>The specific outcome they asked for.</strong> Not what your product does - what they said success looks like.</li>
                    </ul>

                    <h4 className="text-xl font-bold text-gray-900 mt-8 mb-4">Step 2: Use the Right Structure - In the Right Order</h4>
                    <p>
                        The sequence of a winning sales proposal is not the sequence most companies use. Most proposals go: company overview → solution overview → features → pricing → next steps. This is backward.
                    </p>
                    <div className="space-y-4 mt-6">
                        <p><strong>1. The Problem:</strong> One tight paragraph. Reflect their situation back to them.</p>
                        <p><strong>2. The Consequence:</strong> Make the cost of inaction concrete.</p>
                        <p><strong>3. Your Recommended Solution:</strong> Concisely focused on outcomes rather than features.</p>
                        <p><strong>4. Timeline:</strong> A brief timeline of milestones that matter to them.</p>
                        <p><strong>5. Investment:</strong> Don't bury pricing. Put it on page two or three.</p>
                        <p><strong>6. Why Now:</strong> State the competitive window or seasonal factor clearly.</p>
                        <p><strong>7. One Clear Next Step:</strong> A specific, low-friction action.</p>
                    </div>

                    <h4 className="text-xl font-bold text-gray-900 mt-8 mb-4">Step 3: Make the Proposal Look As Good As It Reads</h4>
                    <p>
                        Content wins deals. Design loses them. AI presentation tools like <strong>Deckwale</strong> generate professionally designed pitch-quality proposals with formatting and visual hierarchy that make the deliverable look finished, not rushed.
                    </p>

                    <h4 className="text-xl font-bold text-gray-900 mt-8 mb-4">Step 4: Track What Happens After You Hit Send</h4>
                    <p>
                        What you need to know after you send a proposal:
                    </p>
                    <ul className="list-disc pl-5 space-y-2 mb-6">
                        <li>Did the prospect actually open it?</li>
                        <li>How long did they spend on it?</li>
                        <li>Which sections did they linger on?</li>
                        <li>Did they download or share it?</li>
                    </ul>
                </>
            )
        },
        {
            id: "part-two-board-presentations",
            title: "Part Two: The Complete Guide to Board Presentation Best Practices in 2026",
            content: (
                <>
                    <h4 className="text-xl font-bold text-gray-900 mt-6 mb-4">What Board Members Actually Want</h4>
                    <p>
                        Board members want one thing above everything else: to be equipped to make a good decision, fast. This means the board presentation that impresses is not the most comprehensive one. It's the most useful one.
                    </p>

                    <h4 className="text-xl font-bold text-gray-900 mt-8 mb-4">The 60-Second Rule: Lead with Your Recommendation</h4>
                    <p>
                        Lead with your recommendation in the first 60 seconds. The rest of the presentation is the evidence that supports it.
                    </p>
                    <div className="bg-blue-50 p-6 rounded-xl border border-blue-100 my-6 italic">
                        "We're recommending a £500K expansion into the German market. Based on our Q3 pilot data, we project 3x ROI within 18 months. I'll show you the evidence and address the main risks."
                    </div>

                    <h4 className="text-xl font-bold text-gray-900 mt-8 mb-4">The 10-Slide Framework That Gets Approvals</h4>
                    <ul className="list-decimal pl-5 space-y-2 mb-6">
                        <li><strong>Executive Summary:</strong> Recommendation and key risk.</li>
                        <li><strong>Strategic Context:</strong> Connect to known priorities.</li>
                        <li><strong>The Proposal:</strong> Specifically what is being approved.</li>
                        <li><strong>The Business Case:</strong> Financial argument and assumptions.</li>
                        <li><strong>Implementation Approach:</strong> High-level timeline.</li>
                        <li><strong>Resource Requirements:</strong> People, systems, and costs.</li>
                        <li><strong>Risk Assessment:</strong> Head-on view of major risks.</li>
                        <li><strong>Governance:</strong> Accountability and decision rights.</li>
                        <li><strong>Timeline:</strong> Key milestones for updates.</li>
                        <li><strong>The Ask:</strong> Specific decision requested for a vote.</li>
                    </ul>

                    <h4 className="text-xl font-bold text-gray-900 mt-8 mb-4">The Pre-Wire: The Most Underused Strategy</h4>
                    <p>
                        The best board presentations are never surprises. Send materials at least five days before. Schedule 20-minute calls with key committee members if you expect controversy.
                    </p>
                </>
            )
        },
        {
            id: "part-three-citations",
            title: "Part Three: Why Research Citations in AI-Generated Decks Are Becoming Non-Negotiable for Enterprise Sales",
            content: (
                <>
                    <h4 className="text-xl font-bold text-gray-900 mt-6 mb-4">The Trust Problem Nobody Is Talking About</h4>
                    <p>
                        A 2026 survey found that nearly one in five B2B buyers express lower trust when they sense AI-driven sales interactions. Enterprise buyers are now asking "where did that number come from?" with increasing frequency and less patience.
                    </p>

                    <h4 className="text-xl font-bold text-gray-900 mt-8 mb-4">Why \"The AI Generated It\" Is Not an Answer</h4>
                    <p>
                        The follow-up question - "What's the source for that market size figure?" - determines your credibility. If you don't know, you've lost not just the proposal but the relationship. Grounded claims with attributed sources are your new competitive edge.
                    </p>

                    <h4 className="text-xl font-bold text-gray-900 mt-8 mb-4">What \"Research Citations\" Actually Means in Practice</h4>
                    <ul className="list-disc pl-5 space-y-2 mb-6">
                        <li>Where did this market size figure come from?</li>
                        <li>What year is this data from?</li>
                        <li>Is this primary data or a secondary reference?</li>
                        <li>Can you share the source document?</li>
                    </ul>

                    <h4 className="text-xl font-bold text-gray-900 mt-8 mb-4">How Deckwale Addresses This Problem</h4>
                    <p>
                        <strong>Deckwale</strong> is the only platform that delivers research citations with every generated deck - the actual sources behind the market data and factual claims used to build your slides.
                    </p>
                </>
            )
        },
        {
            id: "conclusion",
            title: "Conclusion: The Modern Professional Presenter",
            content: (
                <>
                    <p>
                        Presentation success in 2026 is not about how impressive the slides look. It's about how defensible the content is and how intelligently you follow up.
                    </p>
                    <p>
                        The through-line connecting all three parts of this guide is the same idea: build better presentations by using AI-generated narratives, research citations, and real-time viewer analytics.
                    </p>
                    <div className="mt-8 p-8 bg-blue-50 rounded-2xl border border-blue-100 text-center">
                        <h3 className="font-syne font-bold text-2xl text-blue-900 mb-4">Build better presentations with Deckwale.</h3>
                        <p className="text-blue-800 mb-6">AI generation, research citations, and real-time viewer analytics. No subscription required.</p>
                        <a href="/" className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-xl transition-colors">
                            Try Deckwale at deckwale.com &rarr;
                        </a>
                    </div>
                </>
            )
        }
    ]
};
